import React from 'react';

export default function PrivacyPolicy({ onBack }) {
  const lastUpdated  = 'April 28, 2026';
  const companyName  = 'TheClearAudit';
  const companyEmail = 'theclearaudit@gmail.com';

  const sections = [
    {
      title: '1. Introduction',
      content: `${companyName} ("we", "us", or "our") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our compliance preparation platform.\n\nPlease read this Privacy Policy carefully. By using the Service you consent to the practices described in this policy.`,
    },
    {
      title: '2. Information We Collect',
      content: `We collect the following types of information:\n\n2.1 Account Information\n• Name and email address provided during registration\n• Password (stored in encrypted form — we never see your actual password)\n• Company name, CAGE code, and contact information you provide in your profile\n\n2.2 Compliance Data\n• Self-assessment answers you enter for CMMC controls\n• Remediation tasks and notes you create\n• Evidence file metadata (file names, control mappings, and statuses — we do not store actual file contents unless you upload them)\n• Annual affirmation records\n• Auditor access tokens you generate\n\n2.3 Usage Data\n• Pages and features you access within the Service\n• Actions you take within the Service\n• Device type, browser type, and operating system\n• IP address and general location\n• Session duration and activity logs\n\n2.4 Payment Information\n• Subscription plan and billing history\n• Payment method type (we do not store full credit card numbers)\n• All payment processing is handled by Stripe Inc. and subject to their privacy policy`,
    },
    {
      title: '3. How We Use Your Information',
      content: `We use the information we collect to:\n\n• Provide, operate, and maintain the Service\n• Process your subscription payments and manage your account\n• Calculate your SPRS score based on your assessment answers\n• Generate SSP, POA&M, and other compliance documents\n• Power the AI assistant feature using anonymized context from your session\n• Send you important account and service notifications\n• Respond to your support requests\n• Improve and develop new features for the Service\n• Comply with legal obligations\n• Detect and prevent fraud or abuse`,
    },
    {
      title: '4. How We Share Your Information',
      content: `We do not sell your personal information. We share your information only in the following circumstances:\n\n4.1 Service Providers\nWe share data with trusted third-party service providers who help us operate the Service:\n• Supabase — database hosting and authentication\n• Stripe — payment processing\n• Third-party AI provider — AI assistant functionality (conversation context only, not your compliance data)\n• Vercel — application hosting\n\nAll service providers are contractually obligated to protect your data and use it only for the purposes we specify.\n\n4.2 Auditor Access\nWhen you generate an auditor access link, the recipient of that link can view your compliance data in read-only mode. You control who receives these links and can revoke access at any time.\n\n4.3 Agency Relationships\nIf you are a client of an Agency plan customer, your compliance data may be visible to your agency administrator as part of the service they provide to you.\n\n4.4 Legal Requirements\nWe may disclose your information if required by law, subpoena, or other legal process, or if we believe disclosure is necessary to protect our rights or the safety of others.\n\n4.5 Business Transfers\nIn the event of a merger, acquisition, or sale of assets, your information may be transferred to the acquiring entity. We will notify you before your information is transferred and becomes subject to a different privacy policy.`,
    },
    {
      title: '5. Data Security',
      content: `We implement industry-standard security measures to protect your data including:\n\n• Encryption of data in transit using TLS/HTTPS\n• Encryption of data at rest in our database\n• Row-level security policies ensuring users can only access their own data\n• Secure authentication with password hashing\n• Regular security reviews\n\nHowever no method of transmission over the internet or electronic storage is 100% secure. While we strive to protect your data we cannot guarantee absolute security. In the event of a data breach we will notify affected users as required by applicable law.`,
    },
    {
      title: '6. Data Retention',
      content: `We retain your data for as long as your account is active or as needed to provide the Service.\n\nIf you delete your account:\n• Your personal information is deleted within 30 days\n• Compliance data is deleted within 30 days\n• Payment records are retained for 7 years as required by tax and accounting laws\n• Anonymized, aggregated usage data may be retained indefinitely\n\nYou may request deletion of your data at any time by contacting us at ${companyEmail}.`,
    },
    {
      title: '7. Your Rights and Choices',
      content: `You have the following rights regarding your data:\n\n7.1 Access — You can access all of your compliance data within the Service at any time.\n\n7.2 Correction — You can update your profile and compliance data directly within the Service.\n\n7.3 Deletion — You can delete your account and all associated data through the Service or by contacting us.\n\n7.4 Export — You can export your compliance data as PDF documents through the Service.\n\n7.5 Opt-out — You can opt out of non-essential communications by contacting us.\n\nTo exercise any of these rights contact us at ${companyEmail}.`,
    },
    {
      title: '8. Cookies and Tracking',
      content: `We use essential cookies and local storage to maintain your login session and remember your preferences. We do not use tracking cookies or sell your data to advertisers.\n\nWe do not display advertisements in the Service. Third-party advertisers cannot access your data through our Service.`,
    },
    {
      title: '9. AI and Machine Learning',
      content: `The AI assistant feature sends your questions and relevant compliance context to our AI processing provider's API to generate responses. We do not send your personal company information or sensitive compliance details to the AI without your knowledge.\n\nAI conversations are not used to train AI models. Conversation data is processed in real time and not permanently stored by our AI provider beyond their standard data retention practices.`,
    },
    {
      title: '10. Children\'s Privacy',
      content: `The Service is not directed to children under 18 years of age. We do not knowingly collect personal information from children. If we become aware that we have collected personal information from a child we will take steps to delete that information.`,
    },
    {
      title: '11. International Data Transfers',
      content: `Your information may be transferred to and processed in countries other than your own. Our service providers including Supabase, Stripe, and Vercel may process data in the United States and other countries. By using the Service you consent to these transfers.`,
    },
    {
      title: '12. California Privacy Rights (CCPA)',
      content: `If you are a California resident you have additional rights under the California Consumer Privacy Act (CCPA) including:\n\n• The right to know what personal information we collect and how it is used\n• The right to delete your personal information\n• The right to opt out of the sale of personal information (we do not sell personal information)\n• The right to non-discrimination for exercising your privacy rights\n\nTo exercise these rights contact us at ${companyEmail}.`,
    },
    {
      title: '13. Changes to This Privacy Policy',
      content: `We may update this Privacy Policy from time to time. We will notify you of material changes by email or by posting a notice within the Service. Your continued use of the Service after changes are posted constitutes acceptance of the updated Privacy Policy.\n\nWe encourage you to review this Privacy Policy periodically.`,
    },
    {
      title: '14. Contact Us',
      content: `If you have questions, concerns, or requests regarding this Privacy Policy or our data practices please contact us:\n\n${companyName}\nPrivacy Inquiries: ${companyEmail}\n\nWe will respond to your inquiry within 30 days.`,
    },
  ];

  return (
    <div style={{
      height: '100vh', background: 'var(--bg)',
      fontFamily: 'var(--font)', color: 'var(--text)',
      overflowY: 'auto',
    }}>
      {/* Header */}
      <div style={{
        background: 'var(--bg2)', borderBottom: '1px solid var(--border)',
        padding: '0 40px', height: 60,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{
            width: 30, height: 30, borderRadius: 7,
            background: 'linear-gradient(135deg, #2D7DD2, #1A4E8A)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 12, fontWeight: 700, color: '#fff',
          }}>CM</div>
          <span style={{ fontSize: 15, fontWeight: 600 }}>TheClearAudit</span>
        </div>
        {onBack && (
          <span
            onClick={onBack}
            style={{
              fontSize: 13, color: 'var(--accent)',
              cursor: 'pointer', textDecoration: 'underline',
            }}
          >
            Back
          </span>
        )}
      </div>

      {/* Content */}
      <div style={{ maxWidth: 800, margin: '0 auto', padding: '48px 40px 80px' }}>

        {/* Title */}
        <div style={{ marginBottom: 40 }}>
          <h1 style={{
            fontSize: 32, fontWeight: 700, color: 'var(--text)',
            marginBottom: 10, letterSpacing: '-0.01em',
          }}>
            Privacy Policy
          </h1>
          <div style={{ fontSize: 13, color: 'var(--text3)' }}>
            Last updated: {lastUpdated}
          </div>
        </div>

        {/* Summary Box */}
        <div style={{
          background: 'var(--green-bg)', border: '1px solid var(--green)',
          borderRadius: 10, padding: 20, marginBottom: 32,
        }}>
          <div style={{
            fontSize: 13, fontWeight: 600, color: 'var(--green)', marginBottom: 10,
          }}>
            Privacy Summary
          </div>
          {[
            'We do not sell your personal information',
            'We do not display advertisements',
            'Your compliance data is encrypted and isolated from other users',
            'You can delete your account and data at any time',
            'AI conversations are not used to train AI models',
            'Payment processing is handled by Stripe — we never see your card number',
          ].map((item, i) => (
            <div key={i} style={{
              display: 'flex', alignItems: 'center', gap: 8,
              fontSize: 12, color: 'var(--green)', marginBottom: 6,
            }}>
              <div style={{
                width: 14, height: 14, borderRadius: '50%', flexShrink: 0,
                background: 'var(--green)', display: 'flex',
                alignItems: 'center', justifyContent: 'center',
                fontSize: 8, fontWeight: 700, color: '#fff',
              }}>✓</div>
              {item}
            </div>
          ))}
        </div>

        {/* Sections */}
        {sections.map((section, i) => (
          <div key={i} style={{ marginBottom: 32 }}>
            <h2 style={{
              fontSize: 16, fontWeight: 600, color: 'var(--text)',
              marginBottom: 12,
            }}>
              {section.title}
            </h2>
            <div style={{
              fontSize: 13, color: 'var(--text2)', lineHeight: 1.8,
              whiteSpace: 'pre-line',
            }}>
              {section.content}
            </div>
            {i < sections.length - 1 && (
              <div style={{ marginTop: 32, borderBottom: '1px solid var(--border)' }} />
            )}
          </div>
        ))}

        {/* Footer */}
        <div style={{
          marginTop: 48, padding: '20px 24px',
          background: 'var(--bg2)', border: '1px solid var(--border)',
          borderRadius: 10, textAlign: 'center',
          fontSize: 12, color: 'var(--text3)', lineHeight: 1.6,
        }}>
          Your privacy matters to us. We are committed to being transparent about our data practices.
          <br />
          Questions? Contact us at <span style={{ color: 'var(--accent)' }}>{companyEmail}</span>
        </div>
      </div>
    </div>
  );
}